import { useEffect, useRef, useState } from 'react';
import type { KeywordGrade } from '@keywordgod/shared';
import { cn } from '@/lib/utils';

/**
 * 원형 SVG 게이지 (0~100). 카운트업 애니메이션 포함.
 * 외곽: 골드 그라데이션. 내부 텍스트: GodScore 큰 숫자 + 등급.
 */
export function GodScoreGauge({
  score,
  grade,
  size = 220,
  thickness = 14,
  durationMs = 1200,
}: {
  score: number;
  grade: KeywordGrade;
  size?: number;
  thickness?: number;
  durationMs?: number;
}) {
  const r = (size - thickness) / 2;
  const circumference = 2 * Math.PI * r;
  const target = Math.max(0, Math.min(100, score));

  const [animated, setAnimated] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = 0;
    const to = target;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimated(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  const dash = (animated / 100) * circumference;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(target)}
      aria-label={`GodScore ${Math.round(target)} / 100`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="god-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFF066" />
            <stop offset="55%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#FFA500" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#1f2937"
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="url(#god-grad)"
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          style={{ transition: 'stroke-dasharray 60ms linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className={cn(
            'tabular font-extrabold leading-none text-gold-gradient',
            size >= 200 ? 'text-6xl' : size >= 140 ? 'text-4xl' : 'text-2xl',
          )}
        >
          {Math.round(animated)}
        </div>
        <div className="mt-1 text-xs uppercase tracking-widest text-gray-400">GodScore</div>
        <div className="mt-2 text-xs font-bold text-gold">{grade}</div>
      </div>
    </div>
  );
}
